import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { TableComponents, TableVirtuoso } from "react-virtuoso";
import { boxStyle } from "../../../consts/styles";
import {
  AreaSummaryFilter,
  memberAreaSummary,
} from "../../../services/dashboard.service";
import { districtService } from "../../../services/master-data.service";

interface Data {
  id: number;
  description?: number;
  total: number;
  total_female: number;
  total_male: number;
  total_type_1: number;
  total_type_2: number;
  total_type_3: number;
  total_type_4: number;
  total_type_5: number;
}

interface ColumnData {
  dataKey: keyof Data;
  label: string;
  numeric?: boolean;
  width: number;
}

var columns: ColumnData[] = [
  {
    width: 100,
    label: "เขต",
    dataKey: "id",
  },
  {
    width: 150,
    label: "จำนวนสมาชิกทั้งหมด",
    dataKey: "total",
  },
  {
    width: 100,
    label: "ชาย",
    dataKey: "total_male",
  },
  {
    width: 100,
    label: "หญิง",
    dataKey: "total_female",
  },
  {
    width: 100,
    label: "ประชาชนทั้วไป",
    dataKey: "total_type_1",
  },
  {
    width: 100,
    label: "นักเรียน",
    dataKey: "total_type_2",
  },
  {
    width: 100,
    label: "อสม.",
    dataKey: "total_type_3",
  },
  {
    width: 150,
    label: "บุคลากรภาคเอกชน",
    dataKey: "total_type_4",
  },
  {
    width: 100,
    label: "บุคลากรภาครัฐ",
    dataKey: "total_type_5",
  },
];

const MemberDescription = ({ areaSummaries, provinces, queryType }: any) => {
  const fixedHeaderContent = () => {
    return (
      <TableRow>
        {columns.map((column: any) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align="center"
            style={{ width: column.width }}
            sx={{
              padding: "10px 0",
              backgroundColor: "#2CADF2",
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }} color="white">
              {column.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const rowContent = (_index: number, row: any) => {
    return (
      <React.Fragment>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align="center"
            sx={{
              borderBottom: "none",
              background: _index % 2 !== 0 ? "#F5F7FA" : "",
            }}
          >
            <Typography align="right" variant="body1" color="black.main">
              {column.dataKey === "total" &&
                (row["total_male"] + row["total_female"]).toLocaleString()}
              {column.dataKey !== "total" &&
                row[column.dataKey]?.toLocaleString()}
            </Typography>
          </TableCell>
        ))}
      </React.Fragment>
    );
  };

  const VirtuosoTableComponents: TableComponents<any> = {
    Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
      <TableContainer
        sx={{ boxShadow: "none", borderRadius: 0 }}
        {...props}
        ref={ref}
      />
    )),
    Table: (props) => (
      <Table
        {...props}
        sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
      />
    ),
    TableHead,
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
      <TableBody {...props} ref={ref} />
    )),
  };

  // states
  const [districts, setDistricts] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [provincesFiltered, setProvincesFiltered] = useState([]);

  // filters
  const [zone, setZone] = useState<string>("");
  const [province, setProvince] = useState<string>("");
  const [district, setDistrict] = useState<string>("");

  // service
  const getDistricts = async (provinceId: string) => {
    setDistrict("");
    const response = await districtService(provinceId);
    setDistricts(response.data);
  };

  const getMemberAreaSummary = () => {
    let label = "";
    if (district) {
      label = "ตำบล";
    } else if (province) {
      label = "อำเภอ";
    } else if (zone) {
      label = "จังหวัด";
    } else {
      label = "เขต";
    }

    columns[0] = {
      width: 100,
      label,
      dataKey: zone || province || district ? "description" : "id",
    };

    memberAreaSummary(
      { zone, province_id: province, district_id: district },
      queryType
    ).then((resp) => {
      setSummaries(resp.data);
      if (!province) {
        const filterByZone = resp.data.map((s: any) => s.description);
        setProvincesFiltered(
          provinces.filter((p: any) => filterByZone.includes(p.name))
        );
      }
    });
  };

  // handle
  const handleZoneChange = (event: SelectChangeEvent) => {
    setZone(event.target.value);
    setProvince("");
    setDistrict("");
    setDistricts([]);
  };

  const handleProvinceChange = (event: SelectChangeEvent) => {
    setProvince(event.target.value);
    setDistrict("");
    getDistricts(event.target.value);
  };

  const handleDistrictChange = (event: SelectChangeEvent) => {
    setDistrict(event.target.value);
  };

  useEffect(() => {
    setSummaries(areaSummaries);
  }, [areaSummaries]);

  useEffect(getMemberAreaSummary, [zone, province, district, queryType]);

  return (
    <Box sx={boxStyle}>
      <Typography
        align="center"
        typography={{ xs: "h4", sm: "h3" }}
        color="black.main"
      >
        รายละเอียดสมาชิก
      </Typography>

      <Box display="flex" gap={{ xs: "8px", sm: "16px" }}>
        <Box
          display="flex"
          gap="6px"
          sx={{ width: { xs: "auto", sm: "100px" } }}
        >
          <Box
            alignSelf="center"
            component="img"
            src="/icons/filter_icon.png"
            width="15px"
            height="10px"
          ></Box>
          <Typography
            alignSelf="center"
            align="center"
            variant="body1"
            color="black.main"
            display={{ xs: "none", sm: "block" }}
          >
            โปรดเลือก
          </Typography>
        </Box>
        <Box display="flex" gap={{ xs: "8px", sm: "16px" }} width={1}>
          <Select
            fullWidth
            sx={{
              "& .MuiSelect-select .notranslate::after": "เขต"
                ? {
                    content: `"เขต"`,
                    opacity: 0.42,
                  }
                : {},
            }}
            value={zone}
            onChange={handleZoneChange}
          >
            <MenuItem key={0} value="">
              ทั้งหมด
            </MenuItem>
            {Array.from(Array(13).keys()).map((index: any) => (
              <MenuItem key={index} value={(index + 1).toString()}>
                {"เขตสุขภาพที่ " + (index + 1)}
              </MenuItem>
            ))}
          </Select>
          <Select
            fullWidth
            sx={{
              "& .MuiSelect-select .notranslate::after": "จังหวัด"
                ? {
                    content: `"จังหวัด"`,
                    opacity: 0.42,
                  }
                : {},
            }}
            value={province}
            onChange={handleProvinceChange}
          >
            <MenuItem key={0} value="">
              ทั้งหมด
            </MenuItem>
            {provincesFiltered.map((p: { id: number; name: string }) => (
              <MenuItem key={p.id} value={p.id.toString()}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
          <Select
            fullWidth
            sx={{
              "& .MuiSelect-select .notranslate::after": "อำเภอ"
                ? {
                    content: `"อำเภอ"`,
                    opacity: 0.42,
                  }
                : {},
            }}
            value={district}
            onChange={handleDistrictChange}
          >
            <MenuItem key={0} value="">
              ทั้งหมด
            </MenuItem>
            {districts.map((d: { id: number; name: string }) => (
              <MenuItem key={d.id} value={d.id.toString()}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box
        sx={{
          height: 400,
          background: "#FFFFFF",
          borderRadius: "10px",
        }}
      >
        <TableVirtuoso
          data={summaries}
          components={VirtuosoTableComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={rowContent}
        />
      </Box>
    </Box>
  );
};

export default MemberDescription;
